import { finishTransferNote, pendingTransferNotes, runTransferNote, type TransferNote, type TransferRecipient } from './keyTransferNote'
import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'
import { type User } from '../../../store/slices/userSlice'
import { fetchUsers } from '../../../store/actionsCreators/userActions'
import { fetchViewings } from '../../../store/actionsCreators/viewingsActions'
import { ManualUserSelector } from '../bsoUnitStatistics/manualOperations/ManualUserSelector'
import '../bsoUnitStatistics/manualOperations/ManualViewingModal.scss'
import { finishKeyOperation, pendingKeyOperations, runKeyOperation, type KeyOperation } from './keyHandover'

export const handoverUserName = (user: User): string => [user.ContactInfo?.FirstName, user.ContactInfo?.FamilyName].filter(Boolean).join(' ') || user.username || `User #${user.id}`

export function usePendingKeyOperations(): KeyOperation[] {
    const [pending, setPending] = useState<KeyOperation[]>(() => pendingKeyOperations())
    useEffect(() => {
        const refresh = (): void => setPending(pendingKeyOperations())
        window.addEventListener('bso:key-operation-progress', refresh)
        window.addEventListener('storage', refresh)
        return () => { window.removeEventListener('bso:key-operation-progress', refresh); window.removeEventListener('storage', refresh) }
    }, [])
    return pending
}

export function PendingKeyOperations({ onRefresh }: { onRefresh: () => Promise<void> }): JSX.Element | null {
    const pending = usePendingKeyOperations()
    const dispatch = useAppDispatch()
    const [saving, setSaving] = useState<string | null>(null)
    const [error, setError] = useState('')
    const [completed, setCompleted] = useState('')
    const resume = async (operation: KeyOperation): Promise<void> => {
        if (saving != null) return
        setSaving(operation.id); setError(''); setCompleted('')
        try {
            await runKeyOperation(operation)
            await onRefresh()
            await dispatch(fetchViewings({ noLading: true }))
            finishKeyOperation(operation.id)
            setCompleted(`Key operation completed and verified. Viewing #${operation.viewingId}, key #${operation.keyId}. Do not create this viewing again.`)
        } catch (e) { setError(e instanceof Error ? e.message : 'Unable to complete the operation.') }
        finally { setSaving(null) }
    }
    if (pending.length === 0) return completed ? <div className="alert alert-success" role="status">{completed}</div> : null
    return <section className="alert alert-warning" aria-live="polite"><h3>Unfinished key operations</h3>
        <p>The receiving booking may already exist. Continue here to reconcile the records without starting a new operation.</p>
        {pending.map((operation) => <div className="d-flex gap-3 align-items-center mb-3" key={operation.id}>
            <span>{operation.kind === 'transfer' ? 'Legacy automatic transfer (creates receiving records): ' : ''}Unit #{operation.unitId} → {operation.recipient.name}{operation.viewingId ? ` · Viewing #${operation.viewingId}` : ''}{operation.keyId ? ` · Key #${operation.keyId}` : ''}</span>
            <button type="button" className="btn btn-sm btn-warning" disabled={saving != null} onClick={() => void resume(operation)}>{saving === operation.id ? 'Continuing…' : 'Continue operation'}</button>
        </div>)}{error && <div role="alert">{error}</div>}
    </section>
}


export function usePendingTransferNotes(): TransferNote[] {
    const [pending, setPending] = useState(pendingTransferNotes)
    useEffect(() => {
        const refresh = (): void => setPending(pendingTransferNotes())
        window.addEventListener('bso:key-operation-progress', refresh)
        window.addEventListener('storage', refresh)
        return () => { window.removeEventListener('bso:key-operation-progress', refresh); window.removeEventListener('storage', refresh) }
    }, [])
    return pending
}

export function PendingTransferNotes({ onRefresh }: { onRefresh: () => Promise<void> }): JSX.Element | null {
    const pending = usePendingTransferNotes()
    const dispatch = useAppDispatch()
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const resume = async (operation: TransferNote): Promise<void> => {
        if (saving) return
        setSaving(true); setError('')
        try {
            await runTransferNote(operation)
            await onRefresh()
            await dispatch(fetchViewings({ noLading: true }))
            finishTransferNote(operation.id)
        } catch (e) { setError(e instanceof Error ? e.message : 'Unable to confirm transfer.') }
        finally { setSaving(false) }
    }
    if (!pending.length) return null
    return <section className="alert alert-warning" role="status">
        <strong>Transfer not confirmed</strong>
        <p>Continue to verify and close the original key only. No viewing or key booking will be created.</p>
        {pending.map((operation) => <div key={operation.id}>Key #{operation.keyId} → {operation.recipient.name}
            <button className="btn btn-sm btn-warning ms-3" type="button" disabled={saving} onClick={() => void resume(operation)}>{saving ? 'Checking…' : 'Continue key action'}</button>
        </div>)}{error && <p role="alert">{error}</p>}
    </section>
}

export function TransferRecipientFields({ recipient, time, disabled, holderId, onRecipient, onTime }: {
    recipient: TransferRecipient, time: string, disabled: boolean, holderId?: number,
    onRecipient: (recipient: TransferRecipient) => void, onTime: (time: string) => void
}): JSX.Element {
    const users = useAppSelector((state) => state.user.allUsers)
    const dispatch = useAppDispatch()
    const [mode, setMode] = useState<'existing' | 'name'>('existing')
    useEffect(() => { if (mode === 'existing' && !users.length) void dispatch(fetchUsers()) }, [mode, users.length, dispatch])
    return <div className="w-100">
        <p>Closes this key only. Create the recipient's viewing separately in Add Viewing.</p>
        <label className="form-label">Recipient
            <select className="form-select" disabled={disabled} value={mode} onChange={(e) => { setMode(e.target.value as typeof mode); onRecipient({ name: '' }) }}>
                <option value="existing">Existing agent</option><option value="name">Enter name manually</option>
            </select>
        </label>
        {mode === 'existing'
            ? <ManualUserSelector users={users.filter((user) => user.Category === 'Agent' && user.id !== holderId)} selectedUserId={recipient.id ?? null} disabled={disabled} onSelect={(user) => onRecipient({ id: user.id, name: handoverUserName(user) })} />
            : <label className="form-label w-100">Recipient name (required)<input className="form-control" required maxLength={200} disabled={disabled} value={recipient.name} onChange={(e) => onRecipient({ name: e.target.value })} /></label>}
        {recipient.id != null && <p>Selected: {recipient.name} (#{recipient.id})</p>}
        <label className="form-label w-100">Transfer time (Dubai)<input className="form-control" type="datetime-local" required disabled={disabled} value={time} onChange={(e) => onTime(e.target.value)} /></label>
    </div>
}
